import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useCurrentUser } from "hooks/api/use-current-user";
import { useBuilderAccess } from "containers/admin/clients/touchpoint/components/builder/use-builder-access";
import { useTouchpointLocking } from "contexts/touchpoint-locking-context";

import {
  useClaimVersionLock,
  useTouchpointVersion,
  type TouchpointVersionAttributes,
} from "hooks/api/use-touchpoint-versions";

import { ConfirmationDialog } from "ui/confirmation-dialog";
import toast from "components/partials/toast/toast";

interface LockingModalTempProps {
  triggerRefresh?: boolean;
  onCloseCallback?: () => void;
  cancelButtonLabel?: string;
}

const LockingModal = ({
  triggerRefresh,
  onCloseCallback,
  cancelButtonLabel = "Cancel",
}: LockingModalTempProps) => {
  const currentClient = useCurrentClient();
  const currentTouchpoint = useCurrentTouchpoint();
  const currentCampaign = useCurrentCampaign();
  const currentUser = useCurrentUser();
  const { accessBuilder } = useBuilderAccess();
  const { setIsLockingModalOpen, onTakeOverEditing, selectedVersion, setOnTakeOverEditing } =
    useTouchpointLocking();
  const claimLockMutation = useClaimVersionLock({
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  });

  const isEnabled = !selectedVersion?.lockedBy || selectedVersion.lockedBy.id === currentUser.id;

  const { data: freshVersion } = useTouchpointVersion(
    {
      clientId: currentClient.id,
      campaignId: currentCampaign.id,
      touchpointId: currentTouchpoint.id,
      id: selectedVersion?.id,
    },
    {
      staleTime: 0,
      refetchOnMount: "always",
      enabled: isEnabled,
    },
  );

  // Gated on `isEnabled`, not on `freshVersion` being undefined: a disabled observer still returns the
  // entry under its key, and the preview route caches this version from before the lock was taken.
  const touchpointVersion =
    (isEnabled ? freshVersion : undefined) ?? (selectedVersion as TouchpointVersionAttributes);

  const handleTakeOverEditing = () => {
    if (!selectedVersion) {
      return;
    }
    claimLockMutation
      .mutateAsync(selectedVersion.id)
      .then(() => {
        setIsLockingModalOpen(false);
        if (onTakeOverEditing) {
          onTakeOverEditing();
        } else {
          accessBuilder(selectedVersion as TouchpointVersionAttributes);
        }
        if (triggerRefresh) {
          window.location.reload();
        }
      })
      .catch((error) => {
        console.error("Failed to take over editing", error);
        toast.error({
          data: {
            title: "Failed to take over editing. Please try again.",
          },
        });
      });
  };

  const handleCloseModal = () => {
    if (onCloseCallback) {
      onCloseCallback();
    } else {
      setIsLockingModalOpen(false);
    }
    setOnTakeOverEditing(null);
  };

  return (
    <ConfirmationDialog
      open
      intent="warning"
      onClose={handleCloseModal}
      onConfirm={handleTakeOverEditing}
      headline={`${touchpointVersion.lockedBy?.firstName ?? "Someone"} ${
        touchpointVersion.lockedBy?.lastName ?? ""
      } is currently working on this content`}
      message="You won't be able to make any changes unless you take over editing. Do you want to take over?"
      cancelButtonText={cancelButtonLabel}
      confirmButtonText="Take over editing"
      closable={false}
    />
  );
};

export default LockingModal;
